import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { FILE_MIME_TYPES } from '@bcba/types';
import { CreateSignupDto } from '../../../services/sign-up/dto/createSignUpDto';
import { postCreateSignup } from '../../../services/sign-up/createSignUp';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useBrowserInterceptor } from '../forms/hooks/useBrowserInterceptor';

export interface SignupFormState extends Partial<CreateSignupDto> {
  step: number;
  companyProvinceUuid?: string;
  companyProvinceName?: string;
  companyLocalityName?: string;
}

interface FormContextType {
  formState: SignupFormState;
  updateForm: (props: FormStateProps) => void;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  signUpRequestUuid?: string;
  getReCaptchaToken: () => Promise<string | undefined>;
  LeaveFormModal: JSX.Element;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FormStateProps {
  formState: SignupFormState;
  shouldSubmitCreation?: boolean;
  onFormUpdateSuccess?: () => void;
}

export const legalRepresentativeDefaultElement = {
  firstName: '',
  lastName: '',
  idDocumentNumber: '',
  idDocumentType: '',
  email: '',
  position: '',
  signupPermissionDocument: undefined,
} as any;

const defaultState: SignupFormState = {
  step: 1,
  companyFiscalId: undefined,
  companyName: '',
  companyStreetName: '',
  companyStreetNumber: undefined,
  companyProvinceId: undefined,
  companyProvinceName: '',
  companyLocalityName: '',
  companyLocalityId: undefined,
  companyPostalCode: undefined,
  companyExchange: undefined,
  companyType: undefined,
  legalRepresentatives: [
    {
      ...legalRepresentativeDefaultElement,
    },
  ],
};

const FormContext = createContext<FormContextType | undefined>(undefined);

interface FormProviderProps {
  children: ReactNode;
  step: number;
}

export const SignUpFormProvider: React.FC<FormProviderProps> = ({
  children,
}) => {
  const [formState, setFormState] = useState<SignupFormState>(defaultState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const { t } = useTranslation();
  const { showErrorToast } = useToastsHook();
  const [signUpRequestUuid, setSignUpRequestUuid] = useState('');

  const { executeRecaptcha } = useGoogleReCaptcha();

  const { LeaveFormModal } = useBrowserInterceptor({
    isFormDirty: isDirty,
  });

  const updateForm = (props: FormStateProps) => {
    const {
      formState,
      shouldSubmitCreation = false,
      onFormUpdateSuccess,
    } = props;
    const updatedFormState = {
      permissionFileDocument: {
        s3TempKey: 'aGVsbG8=',
        name: 'document.pdf',
        mimeType: FILE_MIME_TYPES.PDF,
      },
      ...formState,
    };
    setFormState(updatedFormState);
    if (shouldSubmitCreation && onFormUpdateSuccess) {
      const {
        step: _,
        companyLocalityName,
        companyProvinceName,
        ...rest
      } = formState;

      setIsSubmitting(true);
      postCreateSignup(rest as CreateSignupDto)
        .then(({ uuid }) => {
          setSignUpRequestUuid(uuid);
          onFormUpdateSuccess();
        })
        .catch((_) => {
          showErrorToast({
            title: t('common.toast.error.title'),
            text: t('common.toast.error.text'),
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  // Create an event handler so you can call the verification on button click event or form submit
  const getReCaptchaToken = useCallback(async () => {
    if (!executeRecaptcha) {
      showErrorToast({
        title: t('common.toast.error.title'),
        text: t('common.toast.error.reCaptchaNotAvailable'),
      });
      return;
    }

    return executeRecaptcha('client_sign_up');
  }, [executeRecaptcha, showErrorToast, t]);

  return (
    <FormContext.Provider
      value={{
        formState,
        updateForm,
        isSubmitting,
        setIsSubmitting,
        signUpRequestUuid,
        getReCaptchaToken,
        LeaveFormModal,
        setIsDirty,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useSignupFormContext = () => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};
